import React, { useEffect, useMemo, useState } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Badge, Card, Col, Row, Table, Form } from "react-bootstrap";
import { Link } from "react-router-dom";
import { getToken } from "../services/authorize";
import axiosInstance from "../services/axiosInterceptor";
import { useLoading } from "../contexts/LoadingContext";
import { getCurrentSemester } from "../utils/teachingUtils";
import AdvisorTaskDetail from "./AdvisorTaskDetail";

const API = process.env.REACT_APP_API;

const THAI_MONTHS = [
  "มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน",
  "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม",
];

const DUTIES = [
  { key: "guidance", label: "แนะแนว" },
  { key: "bodymass", label: "นน./ส่วนสูง" },
  { key: "desirable", label: "คุณลักษณะฯ" },
  { key: "opinion", label: "ความเห็นครู" },
  { key: "health", label: "สุขภาพ" },
  { key: "info", label: "ข้อมูล นร." },
];

const STATUS_META = {
  complete: { label: "ครบ", bg: "success" },
  partial: { label: "ไม่ครบ", bg: "warning" },
  none: { label: "ยังไม่ทำ", bg: "danger" },
  no_slot: { label: "ยังไม่กำหนดคาบ", bg: "secondary" },
  na: { label: "-", bg: "light" },
};

// สถานะงานแบบนับ (recorded/total)
const countStatus = ({ recorded, total }) => {
  if (!total) return "na";
  if (recorded === 0) return "none";
  if (recorded < total) return "partial";
  return "complete";
};

// สถานะคาบแนะแนว: อิงจำนวนสัปดาห์ที่เช็คเทียบกับที่ควรเช็ค (expected)
const guidanceStatus = (g) => {
  if (!g) return "no_slot";
  const checked = g.checked_sessions;
  const expected = g.expected_sessions;
  if (expected == null) return checked > 0 ? "complete" : "none"; // ยังไม่มีกรอบเวลา
  if (expected === 0) return "na"; // ยังไม่ถึงสัปดาห์ที่ต้องเช็ค
  if (checked === 0) return "none";
  if (checked < expected) return "partial";
  return "complete";
};

const dutyStatus = (row, key) =>
  key === "guidance" ? guidanceStatus(row.guidance) : countStatus(row[key]);

// ลำดับความรุนแรง (มากไปน้อย) — ใช้เลือก duty เริ่มต้นตอน expand
const SEVERITY = { none: 4, partial: 3, no_slot: 2, complete: 1, na: 0 };

// จำนวนการ์ดต่อแถว ต้องตรงกับ breakpoint ของ <Col xs={12} md={6} xl={4}>
const colsForWidth = (w) => (w >= 1200 ? 3 : w >= 768 ? 2 : 1);

// การ์ดสรุปงานห้อง 1 รายการ สำหรับมุมมองมือถือ
const RoomTaskCard = ({ r, isOpen, onToggle }) => (
  <div className="border rounded p-3" style={{ cursor: "pointer" }} onClick={onToggle}>
    <div className="d-flex align-items-center justify-content-between gap-2">
      <div className="fw-semibold">
        ม.{r.c_id}/{r.r_no}
        {r.p_code && <span className="fs-xs text-muted ms-1">{r.p_code}</span>}
      </div>
      <i className={`ri-arrow-${isOpen ? "down" : "right"}-s-line`}></i>
    </div>
    <div className="fs-xs text-muted">
      {r.advisor_names || "ยังไม่กำหนดครูประจำชั้น"} · นร. {r.total_students} คน
    </div>
    <div className="d-flex flex-wrap gap-2 mt-2 pt-2 border-top">
      {DUTIES.map((d) => (
        <Badge key={d.key} bg={STATUS_META[r.statuses[d.key]].bg}
          className={`fs-xs${r.statuses[d.key] === "na" ? " text-dark" : ""}`}>
          {d.label}: {STATUS_META[r.statuses[d.key]].label}
        </Badge>
      ))}
    </div>
  </div>
);

export default function AdvisorTaskTracking() {
  const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
  const [skin, setSkin] = useState(currentSkin);
  const { setLoading } = useLoading();

  const switchSkin = (s) => {
    const tables = document.getElementsByTagName("table");
    for (const t of tables) {
      const theads = t.querySelectorAll("thead");
      if (s === "dark") {
        for (const th of theads) { th.classList.remove("table-secondary"); th.classList.add("table-primary"); }
        t.classList.add("table-dark");
      } else {
        t.classList.remove("table-dark");
        for (const th of theads) { th.classList.remove("table-primary"); th.classList.add("table-secondary"); }
      }
    }
  };
  useEffect(() => { switchSkin(skin); });

  const [yearList, setYearList] = useState([]);
  const [yearId, setYearId] = useState(null);
  const [semester, setSemester] = useState(getCurrentSemester()); // เริ่มที่ภาคเรียนปัจจุบัน
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [programFilter, setProgramFilter] = useState("all");
  const [onlyPending, setOnlyPending] = useState(false);
  const [rows, setRows] = useState([]);
  const [semWindow, setSemWindow] = useState(null);
  const [expanded, setExpanded] = useState(null); // r_id
  const [activeDuty, setActiveDuty] = useState({}); // r_id -> duty key
  const [view, setView] = useState(() =>
    (typeof window !== 'undefined' && window.innerWidth < 768) ? 'card' : 'table');
  const [cols, setCols] = useState(() =>
    colsForWidth(typeof window !== 'undefined' ? window.innerWidth : 1200));

  useEffect(() => {
    const onResize = () => setCols(colsForWidth(window.innerWidth));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // โหลดปีการศึกษา (เรียง DESC — ค่าเริ่มต้นปีล่าสุด)
  useEffect(() => {
    axiosInstance
      .get(`${API}/getStudyYear`, { headers: { authorization: `Bearer ${getToken()}` } })
      .then((res) => {
        const years = res.data || [];
        setYearList(years);
        if (years.length > 0) setYearId(years[0].year_id);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!yearId) return;
    setLoading(true);
    setExpanded(null);
    axiosInstance
      .get(`${API}/getAdvisorTaskTracking/${yearId}/${semester}?month=${month}`, {
        headers: { authorization: `Bearer ${getToken()}` },
      })
      .then((res) => {
        setRows(res.data?.rows || []);
        setSemWindow(res.data?.semWindow || null);
      })
      .catch(() => { setRows([]); setSemWindow(null); })
      .finally(() => setLoading(false));
  }, [yearId, semester, month]); // eslint-disable-line react-hooks/exhaustive-deps

  // p_code ที่มีอยู่ สำหรับตัวกรองหลักสูตร
  const programs = useMemo(() => {
    const set = new Set(rows.map((r) => r.p_code).filter(Boolean));
    return Array.from(set).sort();
  }, [rows]);

  // แนบสถานะรายห้อง + สรุประดับห้อง
  const withStatus = useMemo(() =>
    rows.map((r) => {
      const statuses = {};
      DUTIES.forEach((d) => { statuses[d.key] = dutyStatus(r, d.key); });
      const actionable = DUTIES
        .map((d) => statuses[d.key])
        .filter((s) => s !== "na" && s !== "no_slot");
      const hasNone = actionable.includes("none");
      const hasPartial = actionable.includes("partial");
      return {
        ...r,
        statuses,
        hasNone,
        hasPartial,
        allComplete: !hasNone && !hasPartial,
      };
    }), [rows]);

  const filtered = useMemo(() => {
    let list = withStatus;
    if (programFilter !== "all") list = list.filter((r) => r.p_code === programFilter);
    if (onlyPending) list = list.filter((r) => r.hasNone || r.hasPartial);
    return list;
  }, [withStatus, programFilter, onlyPending]);

  const kpi = useMemo(() => {
    const c = { total: filtered.length, allComplete: 0, partial: 0, none: 0 };
    filtered.forEach((r) => {
      if (r.hasNone) c.none += 1;
      else if (r.hasPartial) c.partial += 1;
      else c.allComplete += 1;
    });
    return c;
  }, [filtered]);

  const kpiCards = [
    { label: "ห้องเรียนทั้งหมด", value: kpi.total, color: "primary", icon: "ri-community-line" },
    { label: "ครบทุกงาน", value: kpi.allComplete, color: "success", icon: "ri-checkbox-circle-line" },
    { label: "ไม่ครบ", value: kpi.partial, color: "warning", icon: "ri-error-warning-line" },
    { label: "มีงานที่ยังไม่ทำ", value: kpi.none, color: "danger", icon: "ri-close-circle-line" },
  ];

  const roomLabel = (r) => `ม.${r.c_id}/${r.r_no}`;

  const toggleExpand = (r) => {
    if (expanded === r.r_id) { setExpanded(null); return; }
    setExpanded(r.r_id);
    if (!activeDuty[r.r_id]) {
      // เลือก duty ที่สถานะแย่ที่สุดเป็นค่าเริ่มต้น
      let worst = DUTIES[0].key;
      let worstSev = -1;
      DUTIES.forEach((d) => {
        const sev = SEVERITY[r.statuses[d.key]] ?? 0;
        if (sev > worstSev) { worstSev = sev; worst = d.key; }
      });
      setActiveDuty((prev) => ({ ...prev, [r.r_id]: worst }));
    }
  };

  // แผงรายละเอียด (ปุ่มเลือกงาน + ตารางรายคน) ใช้ร่วมกันทั้งมุมมองตารางและการ์ด
  const renderDetail = (r) => (
    <>
      <div className="btn-group btn-group-sm mb-3 flex-wrap">
        {DUTIES.map((d) => {
          const st = r.statuses[d.key];
          const active = (activeDuty[r.r_id] || DUTIES[0].key) === d.key;
          return (
            <button
              key={d.key}
              className={`btn btn${active ? "" : "-outline"}-primary`}
              onClick={() => setActiveDuty((prev) => ({ ...prev, [r.r_id]: d.key }))}
            >
              {d.label}{" "}
              <Badge bg={STATUS_META[st].bg} className={`ms-1${st === "na" ? " text-dark" : ""}`}>
                {STATUS_META[st].label}
              </Badge>
            </button>
          );
        })}
      </div>
      <AdvisorTaskDetail
        duty={activeDuty[r.r_id] || DUTIES[0].key}
        rId={r.r_id}
        yearId={yearId}
        semester={semester}
        month={month}
      />
    </>
  );

  const renderCell = (r, key) => {
    const status = r.statuses[key];
    const meta = STATUS_META[status];
    let detail = null;
    if (key === "guidance") {
      const g = r.guidance;
      if (g) {
        detail = g.expected_sessions != null
          ? `${g.checked_sessions}/${g.expected_sessions} สัปดาห์`
          : `${g.checked_sessions} ครั้ง`;
      }
    } else {
      detail = `${r[key].recorded}/${r[key].total}`;
    }
    return (
      <div className="d-flex flex-column align-items-center">
        <Badge bg={meta.bg} className={`fs-xs${status === "na" ? " text-dark" : ""}`}>{meta.label}</Badge>
        {detail && <span className="fs-xs text-muted mt-1">{detail}</span>}
      </div>
    );
  };

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        {/* Header + filters */}
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="#">หัวหน้าวิชาการสามัญ</Link></li>
              <li className="breadcrumb-item active">ติดตามงานครูประจำชั้น</li>
            </ol>
            <h4 className="main-title mb-0">ติดตามงานครูประจำชั้น</h4>
          </div>
          <div className="d-flex flex-wrap align-items-center gap-2">
            <select
              className="form-select form-select-sm"
              style={{ minWidth: 140 }}
              value={yearId || ""}
              onChange={(e) => setYearId(e.target.value ? Number(e.target.value) : null)}
            >
              {yearList.map((y) => (
                <option key={y.year_id} value={y.year_id}>ปีการศึกษา {y.year_year}</option>
              ))}
            </select>

            <div className="btn-group btn-group-sm">
              {[["1", "ภาคเรียน 1"], ["2", "ภาคเรียน 2"]].map(([v, label]) => (
                <button
                  key={v}
                  className={`btn btn${semester === v ? "" : "-outline"}-primary`}
                  onClick={() => setSemester(v)}
                >{label}</button>
              ))}
            </div>

            <select
              className="form-select form-select-sm"
              style={{ minWidth: 140 }}
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              title="เดือนสำหรับคอลัมน์ น้ำหนัก/ส่วนสูง"
            >
              {THAI_MONTHS.map((m, i) => (
                <option key={i + 1} value={i + 1}>เดือน{m}</option>
              ))}
            </select>

            {programs.length > 1 && (
              <select
                className="form-select form-select-sm"
                style={{ minWidth: 120 }}
                value={programFilter}
                onChange={(e) => setProgramFilter(e.target.value)}
              >
                <option value="all">ทุกหลักสูตร</option>
                {programs.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            )}

            <Form.Check
              type="switch"
              id="only-pending"
              label="เฉพาะห้องที่มีงานค้าง"
              className="fs-sm ms-1"
              checked={onlyPending}
              onChange={(e) => setOnlyPending(e.target.checked)}
            />
          </div>
        </div>

        {/* KPI cards */}
        <Row className="g-3 mb-4">
          {kpiCards.map((k) => (
            <Col key={k.label} xs={6} xl={3}>
              <Card className={`card-one border-${k.color} border-start border-4`}>
                <Card.Body className="p-3">
                  <div className={`text-${k.color} mb-1`}><i className={`${k.icon} fs-40 lh-1`}></i></div>
                  <h6 className="fw-semibold text-dark mb-1">{k.label}</h6>
                  <h3 className={`fw-bold text-${k.color} mb-0`}>{k.value} <span className="fs-sm fw-normal text-muted">ห้อง</span></h3>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {semWindow && !semWindow.sem_first_check && (
          <div className="alert alert-info fs-sm py-2">
            <i className="ri-information-line me-1"></i>
            ยังไม่มีการเช็คชื่อคาบแนะแนวในภาคเรียนนี้ — คอลัมน์แนะแนวจะยังไม่มีตัวหาร (จำนวนสัปดาห์ที่ควรเช็ค)
          </div>
        )}

        {/* Main table */}
        <Card className="card-one">
          <Card.Header className="d-flex flex-wrap align-items-center gap-2">
            <Card.Title as="h6" className="mb-0">สรุปงานรายห้อง</Card.Title>
            <Badge bg="secondary" className="ms-1">{filtered.length} ห้อง</Badge>
            <span className="fs-xs text-muted ms-2">คลิกที่แถวเพื่อดูรายละเอียด</span>
            <div className="btn-group btn-group-sm ms-auto" role="group" aria-label="สลับมุมมอง">
              <button type="button" className={`btn btn${view === 'table' ? '' : '-outline'}-primary`}
                onClick={() => setView('table')} title="มุมมองตาราง">
                <i className="ri-table-line" />
              </button>
              <button type="button" className={`btn btn${view === 'card' ? '' : '-outline'}-primary`}
                onClick={() => setView('card')} title="มุมมองการ์ด">
                <i className="ri-layout-grid-line" />
              </button>
            </div>
          </Card.Header>
          <Card.Body className="p-0">
            {filtered.length === 0 ? (
              <div className="text-center text-muted py-5">
                <i className="ri-inbox-line fs-3 d-block mb-1"></i>ไม่มีข้อมูลตามตัวกรอง
              </div>
            ) : view === 'card' ? (
              <div className="p-3">
                <Row className="g-3">
                  {(() => {
                    const openIdx = filtered.findIndex((x) => x.r_id === expanded);
                    return filtered.map((r, i) => {
                      // แทรกรายละเอียดท้ายแถวที่การ์ดซึ่งถูกกดอยู่ เพื่อไม่ให้ตกไปอยู่ล่างสุดของกริด
                      const lastInRow = (i + 1) % cols === 0 || i === filtered.length - 1;
                      const showDetail =
                        openIdx >= 0 && lastInRow &&
                        Math.floor(openIdx / cols) === Math.floor(i / cols);
                      return (
                        <React.Fragment key={r.r_id}>
                          <Col xs={12} md={6} xl={4}>
                            <RoomTaskCard r={r} isOpen={expanded === r.r_id} onToggle={() => toggleExpand(r)} />
                          </Col>
                          {showDetail && (
                            <Col xs={12}>
                              <div className="bg-body border rounded p-3">
                                <div className="fw-semibold mb-3">{roomLabel(filtered[openIdx])}</div>
                                {renderDetail(filtered[openIdx])}
                              </div>
                            </Col>
                          )}
                        </React.Fragment>
                      );
                    });
                  })()}
                </Row>
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <Table hover responsive className="mb-0 align-middle">
                  <thead className="table-secondary">
                    <tr>
                      <th style={{ width: 40 }}></th>
                      <th style={{ width: 90 }}>ห้อง</th>
                      <th style={{ minWidth: 140 }}>ครูประจำชั้น</th>
                      <th className="text-center" style={{ width: 50 }}>นร.</th>
                      {DUTIES.map((d) => (
                        <th key={d.key} className="text-center" style={{ minWidth: 92 }}>
                          {d.key === "bodymass" ? `นน./ส่วนสูง (${THAI_MONTHS[month - 1]})` : d.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((r) => (
                      <React.Fragment key={r.r_id}>
                        <tr style={{ cursor: "pointer" }} onClick={() => toggleExpand(r)}>
                          <td className="text-center">
                            <i className={`ri-arrow-${expanded === r.r_id ? "down" : "right"}-s-line`}></i>
                          </td>
                          <td>
                            {roomLabel(r)}
                            {r.p_code && <span className="fs-xs text-muted d-block">{r.p_code}</span>}
                          </td>
                          <td className="fs-sm">
                            {r.advisor_names || <span className="text-muted">ยังไม่กำหนดครูประจำชั้น</span>}
                          </td>
                          <td className="text-center">{r.total_students}</td>
                          {DUTIES.map((d) => (
                            <td key={d.key} className="text-center">{renderCell(r, d.key)}</td>
                          ))}
                        </tr>
                        {expanded === r.r_id && (
                          <tr>
                            <td colSpan={4 + DUTIES.length} className="bg-body p-3">
                              {renderDetail(r)}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>

        <Footer />
      </div>
    </React.Fragment>
  );
}
